/**
 * Revision Cloud Tool - Cloud Creation
 * 
 * Creates revision clouds (sketch based).
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export interface RevisionCloudGeometryData {
    boundary: [number, number][];
}

export interface RevisionCloudElement extends Element {
    category: 'Revision Clouds';
    geometryData: RevisionCloudGeometryData;
}

export class RevisionCloudTool extends BaseTool {
    id = 'revision_cloud';
    name = 'Revision Cloud';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);
        this.points.push(snapped);

        // Check if closed
        if (this.points.length > 2) {
            const start = this.points[0];
            // 200mm tolerance (0.2m)
            if (snapped.distanceTo(start) < 0.2) {
                this.points.pop(); // Remove the last point which is same as start
                this.finishSketch();
                return;
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            if (this.points.length >= 3) {
                this.finishSketch();
            }
        } else if (event.key === 'Escape') {
            if (this.points.length > 0) {
                this.points = [];
                this.updatePreview();
            } else {
                super.onKeyDown(event);
            }
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (this.points.length > 0 && this.currentPoint) {
            const previewPoints = [...this.points, this.currentPoint];
            this.context.appStore.setToolPreview({
                type: 'polygon', // Could be cloud shape in future
                points: previewPoints,
                closed: false
                // No fixed color: the viewport draws previews with the
                // theme ink (white in dark mode, black in light mode)
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private finishSketch() {
        if (!this.context) return;

        const boundary: [number, number][] = this.points.map(p => [p.x, p.y]);

        const geometryData: RevisionCloudGeometryData = {
            boundary: boundary
        };

        const cloud: RevisionCloudElement = {
            id: uuidv4(),
            category: 'Revision Clouds',
            type: 'Revision Cloud',
            parameters: {
                'Revision': 'Revision 1',
                'Revision Date': new Date().toISOString().split('T')[0],
                'Description': 'General Revision',
                'Comments': ''
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(cloud);

        this.points = [];
        this.context.appStore.setToolPreview(null);
    }
}
